interface GreCaptcha {
	ready(callback: () => void): void;
	render(
		container: HTMLElement,
		params: {
			sitekey: string;
			callback: (token: string) => void;
			"expired-callback": () => void;
			"error-callback": () => void;
		},
	): number;
	reset(widgetId?: number): void;
}

declare global {
	interface Window {
		grecaptcha?: GreCaptcha;
		dataLayer?: unknown[];
	}
}

const loaded = new Map<string, Promise<void>>();

export function loadScript(src: string, id: string): Promise<void> {
	const existing = loaded.get(src);
	if (existing) return existing;
	const promise = new Promise<void>((resolve, reject) => {
		const script = document.createElement("script");
		script.src = src;
		script.id = id;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () => {
			loaded.delete(src);
			reject(new Error(`failed to load ${src}`));
		};
		document.head.appendChild(script);
	});
	loaded.set(src, promise);
	return promise;
}

export async function loadRecaptcha(): Promise<GreCaptcha> {
	await loadScript("https://www.google.com/recaptcha/api.js?render=explicit", "recaptcha-script");
	const grecaptcha = window.grecaptcha;
	if (!grecaptcha) throw new Error("reCAPTCHA did not initialise");
	await new Promise<void>(resolve => grecaptcha.ready(resolve));
	return grecaptcha;
}

/** Marketing tags of the legacy site, loaded only when configured for the environment. */
export function loadAnalytics(gtmId: string, hubspotPortalId: string): void {
	if (/^GTM-[A-Z0-9]+$/.test(gtmId)) {
		window.dataLayer = window.dataLayer ?? [];
		window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
		void loadScript(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`, "gtm-script").catch(
			() => undefined,
		);
	}
	if (/^\d+$/.test(hubspotPortalId)) {
		void loadScript(`https://js.hs-scripts.com/${hubspotPortalId}.js`, "hs-script-loader").catch(
			() => undefined,
		);
	}
}
